import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Plus, Trash2, Loader2, Search, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { modelsAPI } from '../lib/api';
import { useModelList, invalidateModels } from '../lib/useModelList';
import { apiErrorMessage } from '../lib/apiError';

export const ManageModelsDialog = ({ open, onOpenChange }) => {
    const models = useModelList();
    const [query, setQuery] = useState('');
    const [newName, setNewName] = useState('');
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const handleRemapFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!window.confirm(`Applicare le rinomine del file "${file.name}"?\nColonna A = nome attuale → Colonna B = nuovo nome. Verranno aggiornate commesse e offerte.`)) return;
        setBusy(true);
        try {
            const { data } = await modelsAPI.remap(file);
            toast.success(`Rinomine applicate: ${data.regole} regole · ${data.commesse} telai · ${data.offerte} commesse · ${data.modelli_aggiunti} nuovi modelli in lista`);
            await invalidateModels();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Errore nell\'import delle rinomine'));
        } finally {
            setBusy(false);
        }
    };

    const filtered = useMemo(() => {
        const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        return models.filter(m => { const l = m.name.toLowerCase(); return terms.every(t => l.includes(t)); });
    }, [models, query]);

    const handleAdd = async (e) => {
        e?.preventDefault();
        const name = newName.trim();
        if (!name) return;
        setBusy(true);
        try {
            await modelsAPI.create(name);
            toast.success(`Modello "${name}" aggiunto`);
            setNewName('');
            await invalidateModels();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Errore nell\'aggiunta del modello'));
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (m) => {
        if (!window.confirm(`Togliere "${m.name}" dalla lista? Le commesse che lo usano manterranno il testo.`)) return;
        setBusy(true);
        try {
            const { data } = await modelsAPI.delete(m.id);
            toast.success(data.in_use ? `Modello rimosso (usato in ${data.in_use} commesse, testo mantenuto)` : 'Modello rimosso');
            await invalidateModels();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Errore nella rimozione'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="manage-models-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading uppercase">Gestione modelli veicolo</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAdd} className="flex gap-2">
                    <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Nuovo modello (es. SBA63 STANDARD 3P)"
                        data-testid="manage-models-new-input"
                    />
                    <Button type="submit" className="btn-primary shrink-0" disabled={busy || !newName.trim()} data-testid="manage-models-add">
                        {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />} Aggiungi
                    </Button>
                </form>
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nella lista..." className="pl-9" data-testid="manage-models-search" />
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span><Badge variant="outline">{models.length}</Badge> modelli in lista{query && ` · ${filtered.length} risultati`}</span>
                    <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleRemapFile} data-testid="manage-models-remap-input" />
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={busy} title="Excel: colonna A nome attuale, colonna B nuovo nome" data-testid="manage-models-remap-btn">
                        <FileSpreadsheet size={13} className="mr-1" /> Rinomina da Excel
                    </Button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto border rounded-sm divide-y" data-testid="manage-models-list">
                    {filtered.length === 0 && <p className="p-4 text-sm text-muted-foreground">Nessun modello</p>}
                    {filtered.slice(0, 300).map(m => (
                        <div key={m.id} className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/30" data-testid={`manage-model-row-${m.id}`}>
                            <span className="truncate">{m.name}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" onClick={() => handleDelete(m)} disabled={busy} title="Togli dalla lista" data-testid={`manage-model-delete-${m.id}`}>
                                <Trash2 size={14} />
                            </Button>
                        </div>
                    ))}
                    {filtered.length > 300 && <p className="p-2 text-xs text-muted-foreground">Affina la ricerca per vedere gli altri…</p>}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ManageModelsDialog;
